import React from 'react'
import { REVIEW_SOURCE_NAME, REVIEW_SOURCE_URL } from '../constants/CustomerReviews'

export const JSONLD_SITE_URL = 'https://www.switchboardfree.co.uk'
const ORGANIZATION_NAME = 'SwitchboardFREE'

// Structured data carries no aggregateRating. Our rating comes from Trustpilot, and
// Google's review snippet guidelines rule out ratings aggregated from another website,
// so markup that repeats it risks a structured-data penalty rather than earning stars.
// The same applies to the competitor ratings on the comparison pages. The visible
// rating (SocialProof, the homepage) still reads REVIEW_AGGREGATE directly. This stays
// a function so schema builders keep one place that decides.
export const getAggregateRatingProp = () => ({})

const getReviewSource = () => ({
	'@type': 'WebPage',
	name: `${REVIEW_SOURCE_NAME} company reviews for ${ORGANIZATION_NAME}`,
	url: REVIEW_SOURCE_URL
})

const buildFaq = (questions = []) => ({
	'@context': 'https://schema.org',
	'@type': 'FAQPage',
	mainEntity: questions
		.filter(item => item && item.question && item.answer)
		.map(item => ({
			'@type': 'Question',
			name: item.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.answer
			}
		}))
})

const buildProduct = ({ name, description, image, url, brandName = ORGANIZATION_NAME, offers }) => ({
	'@context': 'https://schema.org',
	'@type': 'Product',
	name,
	description,
	image,
	url,
	brand: {
		'@type': 'Brand',
		name: brandName
	},
	...(offers ? { offers } : {}),
	...getAggregateRatingProp(),
	subjectOf: getReviewSource()
})

const buildService = ({ name, description, url }) => ({
	'@context': 'https://schema.org',
	'@type': 'Service',
	name,
	description,
	url,
	provider: {
		'@type': 'Organization',
		name: ORGANIZATION_NAME,
		url: JSONLD_SITE_URL
	},
	...getAggregateRatingProp(),
	subjectOf: getReviewSource()
})

const buildOrganization = ({ name = ORGANIZATION_NAME, url = JSONLD_SITE_URL, logo, description, address, contactPoint, sameAs = [] }) => ({
	'@context': 'https://schema.org',
	'@type': 'Organization',
	name,
	url,
	logo,
	description,
	address,
	contactPoint,
	sameAs,
	...getAggregateRatingProp(),
	subjectOf: getReviewSource()
})

const buildSchema = ({ type, schemaProps, data }) => {
	if (data) {
		return data
	}

	if (!type) {
		return null
	}

	switch (type) {
		case 'organization':
			return buildOrganization(schemaProps || {})
		case 'service':
			return buildService(schemaProps || {})
		case 'product':
			return buildProduct(schemaProps || {})
		case 'faq':
			return buildFaq((schemaProps && schemaProps.questions) || [])
		default:
			return null
	}
}

// Copy written for dangerouslySetInnerHTML carries HTML entities (&amp;, &pound;), and so do
// WordPress titles. JSON-LD is JSON, not HTML, so a reader takes the entity text literally.
// A textarea decodes entities and leaves any tags in the string as written.
export const decodeEntities = value => {
	if (typeof value === 'string') {
		if (!value.includes('&')) {
			return value
		}
		const textarea = document.createElement('textarea')
		textarea.innerHTML = value
		return textarea.value
	}
	if (Array.isArray(value)) {
		return value.map(decodeEntities)
	}
	if (value && typeof value === 'object') {
		return Object.keys(value).reduce((decoded, key) => ({ ...decoded, [key]: decodeEntities(value[key]) }), {})
	}
	return value
}

const JsonLd = ({ type, schemaProps, data }) => {
	const schema = buildSchema({ type, schemaProps, data })
	// As a text child React escapes quotes to &quot;, which a parser reading the served HTML
	// takes literally inside <script>. Writing the JSON as innerHTML keeps it valid, and escaping
	// '<' stops a value such as a blog title from closing the script element.
	const json = schema ? JSON.stringify(decodeEntities(schema)).replace(/</g, '\\u003c') : null
	const script = React.useRef(null)

	// Hydrating a prerendered page keeps the script's build-time content, so values computed
	// at render time, such as an offer's priceValidUntil, are written in once the page is live.
	React.useEffect(() => {
		if (script.current && script.current.innerHTML !== json) {
			script.current.innerHTML = json
		}
	}, [json])

	if (!json) {
		return null
	}

	return <script ref={script} type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />
}

export default JsonLd
